'use client'

import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { usePathname, useRouter } from 'next/navigation'

import { Logo, WebFormEventPresenter } from 'Components/Blocks'
import { Button, Column, Loader, Text } from 'Components/UI'
import { EventItem } from 'Types/emajinAPI'
import Utils from 'Utils'

import { EVENTS } from 'Constants/Paths'

import APIService from 'Services/API'

import { RSVPProvider } from './context'
import { Container, Content, EventHolder, FormHolder } from './styles'

function RSVPLayout({ children }: PropsWithChildren) {
  const pathname = usePathname()
  const router = useRouter()

  const [event, setEvent] = useState<EventItem | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const eventId = Utils.Event.extractEventId(pathname)

  const handleOpenEvents = useCallback(() => {
    router.replace(EVENTS)
  }, [router])

  const handleBack = useCallback(() => {
    router.back()
  }, [router])

  useEffect(() => {
    if (!eventId) {
      setLoading(false)
      setError(true)
      return
    }

    setLoading(true)
    setError(false)
    setEvent(undefined)

    APIService.getEvent(eventId)
      .then(result => {
        setEvent(result.data.event)
      })
      .catch(() => {
        setError(true)
      })
      .finally(() => setLoading(false))
  }, [eventId])

  if (loading)
    return (
      <Container>
        <Loader inverse />
      </Container>
    )

  if (!event && !error) return null

  return (
    <Container>
      <Column alignCenter fullHeight gap={[9, 9, 11]} px={3} py={9}>
        <Logo />

        <Column grow justifyCenter overflow="hidden">
          <Content
            flexDirection={['column', 'column', 'column', 'column', 'row']}
            width={['100%', '100%', '100%', '100%', '880px']}
          >
            {error ? (
              <FormHolder width="100%">
                <Column alignCenter gap={6}>
                  <Text body1>Something goes wrong</Text>

                  <Button secondary small width="120px" onClick={handleBack}>
                    Back
                  </Button>
                </Column>
              </FormHolder>
            ) : (
              <>
                <EventHolder>
                  <WebFormEventPresenter event={event} />
                </EventHolder>

                <RSVPProvider
                  values={{
                    event,
                    onOpenEvents: handleOpenEvents,
                  }}
                >
                  <FormHolder width={['100%', '100%', '100%', '100%', '360px']}>
                    {children}
                  </FormHolder>
                </RSVPProvider>
              </>
            )}
          </Content>
        </Column>
      </Column>
    </Container>
  )
}

export default RSVPLayout
